import React, {useEffect, useState} from 'react';
import styled, {css} from 'styled-components';
import {useDispatch, useSelector} from 'react-redux';
import {loadGames} from '../store/actions/games';
import {selectDisplayGameFullScreen, selectGames} from '../store/reducers';
import Loader from '../components/Loader';
import {useRouteMatch} from 'react-router-dom';
import {Flex, mobileMediaQuery, useIsWeb, breakpoints} from '../styles';
import {
    showAnimation,
    closeAnimation,
} from '../components/UnlockModal/animations';
import Button from '../components/Button';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import {ReactComponent as CloseIcon} from '../assets/close.svg';
import ContentRow from '../components/ContentRow';
import {debugCNitoUrl} from '../helpers';
import GamesGrid from '../components/GamesGrid';
import {useWindowSize} from '../hooks/useWindowSize';
import {saveGamePlayed} from '../store/actions/user';
import {analyticsTrackState} from '../helpers/Analytics';
import {getCurrentLang} from '../utils/i18n';
import Helmet from 'react-helmet';

const DKidsGameDetails = () => {
    const games = useSelector(selectGames);
    const dispatch = useDispatch();
    const history = useHistory();
    const [currentGame, setCurrentGame] = useState(undefined);
    const [showExitModal, setShowExitModal] = useState(false);
    const [hideExitModal, setHideExitModal] = useState(true);
    const {width, height} = useWindowSize();
    const isWeb = useIsWeb();
    const isMobile =
        width <= breakpoints.mobile || height <= breakpoints.mobile;

    const {t} = useTranslation();
    const [relatedGames, setRelatedGames] = useState([]);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const displayInFullScreen = useSelector(selectDisplayGameFullScreen);

    //landscape is only relevant for mobile and fullscreen
    const isMobileLandscape = isMobile && isFullScreen ? width > height : false;

    const {
        params: {slug},
    } = useRouteMatch();

    useEffect(() => {
        if (!games) {
            dispatch(loadGames());
        }

        if (displayInFullScreen) {
            toggleFullscreen(); //start in full screen
        }

        return () => {
            //reset fullscreen state
            const body = document.querySelector('body');
            body.style.overflow = 'auto';
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (games) {
            const game = games.find(g => g.slug === slug);
            if (game) {
                dispatch(saveGamePlayed(game.id));
                setCurrentGame(game);
                setRelatedGames(
                    games.filter(
                        g =>
                            (g.internationalCategory ===
                                game.internationalCategory ||
                                g.showName === game.showName) &&
                            g.id !== game.id,
                    ),
                );
                analyticsTrackState('game', {
                    section: 'Games',
                    contenttype: 'Game',
                    contenttitle: game.title,
                });
            } else {
                history.push(`/${debugCNitoUrl()}`);
            }
        }
    }, [dispatch, games, history, slug]);

    useEffect(() => {
        setTimeout(
            () => {
                setHideExitModal(!showExitModal);
            },
            showExitModal ? 0 : 500,
        );
    }, [showExitModal]);

    const handleCloseGame = () => {
        setShowExitModal(!showExitModal);
    };

    const handleConfirmClose = (confirm = false) => {
        setShowExitModal(false);
        if (confirm) {
            if (displayInFullScreen) {
                history.push(`/${debugCNitoUrl()}`);
            } else {
                toggleFullscreen();
            }
        }
    };

    const toggleFullscreen = () => {
        if (isFullScreen) {
            setIsFullScreen(false);
            const body = document.querySelector('body');
            body.style.overflow = 'auto';
        } else {
            setIsFullScreen(true);

            const body = document.querySelector('body');
            window.scrollTo(0, 0);
            body.style.overflow = 'hidden';
        }
    };

    const handleClickGame = (id, game) => {
        const link = getCurrentLang() === 'es' ? 'juegos/' : 'jogos/';
        history.push(`${link}${game.slug}${debugCNitoUrl()}`);
    };

    return (
        <>
            <Helmet>
                <title>Discovery Kids</title>
            </Helmet>
            <Container isFullScreen={isFullScreen}>
                {!currentGame && <Loader />}
                {currentGame && (
                    <>
                        <GameContainer
                            isFullScreen={isFullScreen}
                            isLandscape={isMobileLandscape}
                        >
                            {isFullScreen && (
                                <CloseButton
                                    onClick={handleCloseGame}
                                    isLandscape={isMobileLandscape}
                                >
                                    <CloseIcon />
                                </CloseButton>
                            )}
                            <GamePlayer
                                src={currentGame.url}
                                title={currentGame.title}
                                isFullScreen={isFullScreen}
                                orientation={currentGame.orientation}
                            />
                            {!isFullScreen && (
                                <GameTitle>{currentGame.title}</GameTitle>
                            )}
                            {!isFullScreen && (
                                <GameDescription>
                                    {currentGame.description}
                                </GameDescription>
                            )}
                            {!isFullScreen && (
                                <GameTapArea onClick={toggleFullscreen} />
                            )}
                            {!hideExitModal && (
                                <ExitModal
                                    direction="column"
                                    show={showExitModal}
                                >
                                    <h2>{t('games.exitModal.title')}</h2>

                                    <p>{t('games.exitModal.subtitle')}</p>

                                    <Button
                                        width="280px"
                                        margin="0 0 16px 0"
                                        background="#2535FA"
                                        value={t('games.exitModal.keepPlaying')}
                                        onClick={() =>
                                            handleConfirmClose(false)
                                        }
                                        dkids
                                    />

                                    <Button
                                        width="280px"
                                        background="#DBDEFF"
                                        color="#2535FA"
                                        value={t('games.exitModal.exitGame')}
                                        onClick={() => handleConfirmClose(true)}
                                        dkids
                                    />
                                </ExitModal>
                            )}
                        </GameContainer>
                        {relatedGames && relatedGames.length > 0 && isWeb && (
                            <CustomContentRow
                                title={'Related Games'}
                                titleAlign="left"
                                items={relatedGames}
                                onClickContent={handleClickGame}
                                type="games"
                            />
                        )}
                        {relatedGames && relatedGames.length > 0 && !isWeb && (
                            <>
                                <SectionTitle>{'Related Games'}</SectionTitle>
                                <CustomGrid
                                    items={relatedGames}
                                    hidePlayButton
                                    onClickItem={handleClickGame}
                                    brand={'dkids'}
                                />
                            </>
                        )}
                    </>
                )}
            </Container>
        </>
    );
};

const Container = styled.div`
    width: 100%;
    min-height: 800px;
    align-items: center;
    display: flex;
    flex-direction: column;

    padding-top: ${props => (props.isFullScreen ? 0 : 90)}px;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #0d1744 100%),
        url(${require('../assets/dkids/background_big.png')}),
        linear-gradient(180deg, #2d3bbc 0%, #121f97 100%);

    background-size: 100% 400px, cover, cover;
    background-repeat: no-repeat;
    background-position: bottom, center center, center center;
`;

const GameContainer = styled.div`
    position: relative;
    width: 1068px;
    display: flex;
    flex-direction: ${props => (props.isLandscape ? 'row-reverse' : 'column')};
    max-width: calc(100% - 80px);
    display: flex;
    align-items: center;
    justify-content: center;
    ${props =>
        props.isFullScreen &&
        css`
            position: absolute;
            width: 100%;
            height: 100vh;
            background: #000;
            z-index: 100;
            max-width: 100%;
            top: 0px;
            :global(body) {
                overflow: hidden;
            }
        `};

    ${mobileMediaQuery} {
        max-width: ${props =>
            props.isFullScreen ? '100%' : 'calc(100% - 50px)'};
    }
`;
const GamePlayer = styled.iframe`
    width: ${props => (props.orientation === 'landscape' ? '100%' : '100%')};
    height: ${props =>
        props.orientation === 'landscape' ? 'min(650px, 61vw)' : '600px'};
    border: none;
    overflow: hidden;
    align-self: center;
    ${props =>
        props.isFullScreen &&
        css`
            align-self: flex-start;
            width: 100%;
            height: ${props =>
                props.orientation === 'landscape'
                    ? '80vh'
                    : 'calc(100vh - 136px)'};
            margin-bottom: 68px;
        `};
    background: #000;
`;

const GameTitle = styled.div`
    font-family: Mont;
    font-weight: 950;
    font-size: 20px;
    text-transform: uppercase;
    margin-top: 24px;
    align-self: flex-start;
`;

const GameDescription = styled.div`
    font-family: Mont;
    font-weight: 100;
    font-size: 16px;
    align-self: flex-start;
`;

const CustomContentRow = styled(ContentRow)`
    margin-top: 50px;
    max-width: 1440px;
    margin-bottom: 44px;
`;

const GameTapArea = styled.div`
    width: 100%;
    height: 100%;
    position: absolute;
`;

const ExitModal = styled(Flex)`
    position: absolute;
    padding: 40px;
    border-radius: 16px;
    background-color: #fff;
    box-sizing: border-box;
    width: 535px;
    height: auto;
    max-width: 90%;

    ${props =>
        !props.show
            ? css`
                  transform: translateY(100vh);
                  animation-name: ${closeAnimation};
                  animation-duration: 0.5s;
                  animation-timing-function: cubic-bezier(0.22, 0.61, 0.35, 1);
                  animation-fill-mode: forwards;
              `
            : css`
                  animation-name: ${showAnimation};
                  animation-duration: 0.5s;
                  animation-timing-function: cubic-bezier(0.22, 0.61, 0.35, 1);
                  animation-fill-mode: forwards;
              `};

    h2,
    p {
        color: #000;
    }

    h2 {
        font-family: 'Dkids';
        font-size: 20px;
        line-height: 1.125;
        font-weight: bold;
        text-align: center;
        text-transform: uppercase;
        margin: 0 0 16px 0;
    }

    p {
        /* font-family: 'AvenirNextRoundedStd'; */
        font-size: 18px;
        line-height: 24px;
        text-align: center;
        max-width: 316px;
        margin: 0 0 32px 0;
    }

    @media screen and (max-width: 380px) {
        padding: 40px 20px;
    }
`;

const CloseButton = styled.div`
    margin: 20px;
    width: 28px;
    height: 28px;
    g {
        fill: #fff;
    }
    pointer-events: all;
    cursor: pointer;
    align-self: ${props => (props.isLandscape ? 'flex-start' : 'flex-end')};
`;

const CustomGrid = styled(GamesGrid)`
    max-width: 1360px;
    align-self: center;
    margin-bottom: 40px;
`;

const SectionTitle = styled.div`
    font-family: Mont;
    font-weight: 900;
    font-style: normal;
    font-size: 20px;
    line-height: 26px;
    text-transform: uppercase;
    margin: 16px 0px 16px 40px;

    align-self: flex-start;
    ${mobileMediaQuery} {
        margin: 16px 0px 16px 25px;
    }

    @media screen and (min-width: ${breakpoints.xl}px) {
        align-self: center;
        max-width: 1360px;
        margin: 16px 0px;
        text-align: left;
        width: 100%;
    }
`;

export default DKidsGameDetails;
